import React, { useRef } from 'react'
import classnames from 'classnames'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AttachFileIcon from '@material-ui/icons/AttachFile'
import type { Editor } from '@tiptap/react'
import CloseIcon from '@material-ui/icons/Close'
import { Tooltip } from '@material-ui/core'
import LinkIcon from '@material-ui/icons/Link'
import LinkOffIcon from '@material-ui/icons/LinkOff'
import CheckIcon from '@material-ui/icons/Check'
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted'
import FormatListNumberedIcon from '@material-ui/icons/FormatListNumbered'
import FormatAlignLeftIcon from '@material-ui/icons/FormatAlignLeft'
import FormatAlignCenterIcon from '@material-ui/icons/FormatAlignCenter'
import FormatColorTextIcon from '@material-ui/icons/FormatColorText'
import FormatAlignRightIcon from '@material-ui/icons/FormatAlignRight'
import FormatAlignJustifyIcon from '@material-ui/icons/FormatAlignJustify'
import HighlightIcon from '@material-ui/icons/BorderColor'
import { useSnackbar } from 'notistack'
import { useTranslation } from '@paintscout/ui'
import Typography from '@paintscout/ui/components/Typography'

import { InlineColorPicker } from '@paintscout/ui/components/ColorPicker'
import type { EditorProps } from './Editor'
import { EditorTemplateMenu } from './EditorTemplateMenu'
import {
  saveSelection as saveEditorSelection,
  clearPersistentSelection as clearEditorPersistentSelection,
  applyTextStyleColor,
  applyHighlightColor
} from './editorColorCommands'

export interface TipTapToolbarProps extends EditorProps {
  editor: Editor
}

const ALIGN_FLEX_START = 'flex-start'

const useStyles = makeStyles<Theme>((theme) => {
  return {
    tippyMenu: (props: TipTapToolbarProps) => ({
      pointerEvents: props.disabled ? 'none' : 'auto',
      opacity: props.disabled ? 0.5 : 1.0,
      backgroundColor: theme.palette.common.white,
      padding: 0,
      overflow: 'hidden',
      display: 'flex',
      maxWidth: 180,
      flexWrap: 'wrap',
      flexGrow: 0,
      '& button': {
        background: 'none',
        border: `none`,
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.pxToRem(16),
        minWidth: theme.typography.pxToRem(30),
        display: 'flex',
        maxHeight: theme.spacing(4),
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(0.5),
        fontWeight: theme.typography.fontWeightMedium,
        color: theme.paintscout.palette.brand.Neutral.P50,
        cursor: 'pointer',
        '& svg': {
          fontSize: '1rem'
        },
        '&:hover': {
          color: theme.paintscout.palette.brand.Neutral.P40
        },
        '&.is-active': {
          backgroundColor: theme.paintscout.palette.brand.Neutral.P90
        },
        '&.bold': {
          fontWeight: theme.typography.fontWeightBold
        },
        '&.italic': {
          fontWeight: theme.typography.fontWeightBold,
          fontStyle: 'italic'
        },
        '&.underline': {
          fontWeight: theme.typography.fontWeightBold,
          textDecoration: 'underline'
        }
      }
    }),
    tippyMenuBar: {
      position: 'sticky',
      zIndex: 2,
      '&$tippyMenuBar': {
        maxWidth: '100%',
        // maxHeight: theme.spacing(4),
        width: '100%',
        alignSelf: ALIGN_FLEX_START,
        boxShadow: theme.shadows[1]
      },
      '& $linkInput': {
        width: 'auto'
      }
    },
    linkInput: {
      width: '100%',
      border: 'none'
    },
    linkInputWrapper: {
      '&$linkInputWrapper': {
        display: 'flex',
        alignContent: ALIGN_FLEX_START,
        flexGrow: 0
      }
    },
    attachFileButton: {
      backgroundColor: 'none',
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(0.5),
      color: theme.paintscout.palette.brand.Neutral.P50,
      '&:hover': {
        cursor: 'pointer',
        color: theme.paintscout.palette.brand.Neutral.P40
      }
    },
    attachFileButtonDiv: {
      alignSelf: 'flex-end',
      marginLeft: 'auto',
      paddingBottom: '2px',
      [theme.breakpoints.down(333)]: {
        display: 'none',
        alignItems: ALIGN_FLEX_START,
        width: '100%'
      }
    },
    attachIcon: {
      fontSize: theme.typography.pxToRem(16),
      marginBottom: theme.spacing(0.25),
      alignSelf: ALIGN_FLEX_START
    },
    attachFileText: {
      [theme.breakpoints.down('md')]: {
        display: 'none'
      },
      [theme.breakpoints.down('sm')]: {
        display: 'block'
      },
      [theme.breakpoints.down(425)]: {
        display: 'none'
      }
    },
    pathFillIcon: {
      '& path': {
        fillOpacity: 1
      }
    }
  }
})

export function TipTapToolbar(props: TipTapToolbarProps) {
  const { editor, onReset, templateContext, disabled, onAddFiles, onClear, blockColor } = props
  const classes = useStyles(props)
  const { enqueueSnackbar } = useSnackbar()
  const { __ } = useTranslation()

  const defaultToolbar = {
    headings: false,
    bold: true,
    italic: true,
    underline: true,
    color: true,
    highlight: true,
    align: false,
    lists: true,
    link: true,
    template: null
  }
  const toolbar = {
    ...defaultToolbar,
    ...props.toolbar
  }

  const placeholders = toolbar?.template?.tags || []
  const hrefInput = useRef(null)
  const savedSelectionRef = useRef<{ from: number; to: number } | null>(null)

  const saveSelection = () => saveEditorSelection(editor, savedSelectionRef)
  const clearPersistentSelection = () => clearEditorPersistentSelection(editor)

  const isActive = (...params) => {
    if (disabled) {
      return false
    }
    // eslint-disable-next-line prefer-spread
    return editor.isActive.apply(editor, params)
  }

  // Maintain current text align status to pass in on font change
  const alignmentOptions = {
    left: isActive({ textAlign: 'left' }),
    center: isActive({ textAlign: 'center' }),
    right: isActive({ textAlign: 'right' }),
    justify: isActive({ textAlign: 'justify' })
  }
  const currentTextAlign = Object.keys(alignmentOptions).find((key) => alignmentOptions[key] === true)
  const blockBlur = useRef<boolean>(false)
  const handleLinkSet = (showToast: boolean = false) => {
    const inputValue = hrefInput.current?.value?.trim()
    // If input is empty or just the placeholder, remove the link
    if (!inputValue || inputValue === 'https://example.com') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      if (hrefInput.current) {
        hrefInput.current.value = ''
      }
      return
    }
    let href = inputValue
    if (!/^https?:\/\//i.test(href)) {
      href = 'https://' + href
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href }).run()
    if (showToast) {
      setTimeout(() => {
        if (editor?.getAttributes('link')?.href) {
          enqueueSnackbar(__('Hyperlink updated successfully'), { variant: 'success' })
        }
      }, 0)
    }
    if (hrefInput.current) {
      hrefInput.current.value = ''
    }
  }
  const showCloseIconFunctions = onClear && (props?.files?.length > 0 || !editor.isEmpty)

  return (
    <div id={'menu-bar'} className={classnames([classes.tippyMenu, classes.tippyMenuBar, 'MenuBar'])}>
      {toolbar.bold && (
        <button
          type="button"
          onClick={(ev) => {
            ev.preventDefault()
            editor.chain().focus().toggleMark('bold').run()
          }}
          className={isActive('bold') ? 'is-active bold' : 'bold'}
        >
          B
        </button>
      )}
      {toolbar.italic && (
        <button
          type="button"
          onClick={(ev) => {
            ev.preventDefault()
            editor.chain().focus().toggleMark('italic').run()
          }}
          className={isActive('italic') ? 'is-active italic' : 'italic'}
        >
          I
        </button>
      )}
      {toolbar.underline && (
        <button
          type="button"
          onClick={(ev) => {
            ev.preventDefault()
            editor.chain().focus().toggleUnderline().run()
          }}
          className={isActive('underline') ? 'is-active underline' : 'underline'}
        >
          U
        </button>
      )}
      {toolbar?.color && !blockColor && (
        <InlineColorPicker
          swatchIcon={
            <button
              type="button"
              onMouseDown={(ev) => {
                ev.preventDefault()
              }}
            >
              <FormatColorTextIcon
                style={{ color: editor.getAttributes('textStyle').color }}
                className={classes.pathFillIcon}
              />
            </button>
          }
          swatchOnly
          value={editor.getAttributes('textStyle').color}
          onOpen={saveSelection}
          onClose={clearPersistentSelection}
          onChange={(color: string) => applyTextStyleColor(editor, savedSelectionRef, color)}
          useClearOption
          defaultColor={''}
          onClear={() => applyTextStyleColor(editor, savedSelectionRef, null)}
        />
      )}
      {toolbar?.highlight && !blockColor && (
        <InlineColorPicker
          swatchIcon={
            <button
              type="button"
              onMouseDown={(ev) => {
                ev.preventDefault()
              }}
            >
              <HighlightIcon style={{ color: editor.getAttributes('highlight').color }} />
            </button>
          }
          swatchOnly
          value={editor.getAttributes('highlight').color ?? ''}
          onOpen={saveSelection}
          onClose={clearPersistentSelection}
          onChange={(color: string) => applyHighlightColor(editor, savedSelectionRef, color)}
          useClearOption
          defaultColor={''}
          onClear={() => applyHighlightColor(editor, savedSelectionRef, null)}
          hideSelectionHighlight
        />
      )}
      {toolbar.headings && (
        <>
          <button
            type="button"
            onClick={(ev) => {
              ev.preventDefault()
              editor
                .chain()
                .focus()
                .toggleNode('heading', 'paragraph', { level: 1 })
                .setTextAlign(currentTextAlign)
                .run()
            }}
            className={isActive('heading', { level: 1 }) ? 'is-active' : ''}
          >
            H1
          </button>
          <button
            type="button"
            onClick={(ev) => {
              ev.preventDefault()
              editor
                .chain()
                .focus()
                .toggleNode('heading', 'paragraph', { level: 2 })
                .setTextAlign(currentTextAlign)
                .run()
            }}
            className={isActive('heading', { level: 2 }) ? 'is-active' : ''}
          >
            H2
          </button>
          <button
            type="button"
            onClick={(ev) => {
              ev.preventDefault()
              editor
                .chain()
                .focus()
                .toggleNode('heading', 'paragraph', { level: 3 })
                .setTextAlign(currentTextAlign)
                .run()
            }}
            className={isActive('heading', { level: 3 }) ? 'is-active' : ''}
          >
            H3
          </button>
        </>
      )}
      {toolbar.align && (
        <>
          <button
            type="button"
            onClick={(ev) => {
              ev.preventDefault()
              editor.chain().focus().setTextAlign('left').run()
            }}
            className={isActive({ textAlign: 'left' }) ? 'is-active' : ''}
          >
            <FormatAlignLeftIcon />
          </button>
          <button
            type="button"
            onClick={(ev) => {
              ev.preventDefault()
              editor.chain().focus().setTextAlign('center').run()
            }}
            className={isActive({ textAlign: 'center' }) ? 'is-active' : ''}
          >
            <FormatAlignCenterIcon />
          </button>
          <button
            type="button"
            onClick={(ev) => {
              ev.preventDefault()
              editor.chain().focus().setTextAlign('right').run()
            }}
            className={isActive({ textAlign: 'right' }) ? 'is-active' : ''}
          >
            <FormatAlignRightIcon />
          </button>
          <button
            type="button"
            onClick={(ev) => {
              ev.preventDefault()
              editor.chain().focus().setTextAlign('justify').run()
            }}
            className={isActive({ textAlign: 'justify' }) ? 'is-active' : ''}
          >
            <FormatAlignJustifyIcon />
          </button>
        </>
      )}
      {toolbar.lists && (
        <>
          <button
            type="button"
            onClick={(ev) => {
              ev.preventDefault()
              editor.chain().focus().toggleList('bulletList', 'listItem').run()
            }}
            className={isActive('bulletList') ? 'is-active' : ''}
          >
            <FormatListBulletedIcon />
          </button>
          <button
            type="button"
            onClick={(ev) => {
              ev.preventDefault()
              editor.chain().focus().toggleList('orderedList', 'listItem').run()
            }}
            className={isActive('orderedList') ? 'is-active' : ''}
          >
            <FormatListNumberedIcon />
          </button>
        </>
      )}
      {toolbar.link && (
        <button
          type="button"
          onClick={(ev) => {
            ev.preventDefault()
            if (isActive('link')) {
              editor.chain().focus().extendMarkRange('link').unsetLink().run()
            } else {
              // Create a new link with placeholder href
              editor.chain().focus().toggleLink({ href: 'https://example.com' }).run()
            }
          }}
          className={isActive('link') ? 'is-active' : ''}
        >
          {isActive('link') ? <LinkOffIcon /> : <LinkIcon />}
        </button>
      )}
      <div className={classes.linkInputWrapper}>
        {isActive('link') && (
          <>
            <input
              ref={hrefInput}
              placeholder={editor.getAttributes('link').href || __('Enter URL...')}
              className={classes.linkInput}
              type={'text'}
              onFocus={(e) => {
                if (hrefInput.current) {
                  const currentHref = editor.getAttributes('link')?.href || ''
                  // Always set the value to the current href
                  hrefInput.current.value = currentHref
                  // Select all text so user can immediately type to replace it (one backspace clears placeholder)
                  e.target.select()
                }
              }}
              onBlur={() => {
                setTimeout(() => {
                  if (!blockBlur.current && hrefInput.current) {
                    const inputValue = hrefInput.current.value?.trim()
                    if (inputValue || editor?.getAttributes('link')?.href) {
                      handleLinkSet()
                    }
                  } else {
                    blockBlur.current = false
                  }
                }, 0)
              }}
            />
            <button
              type="button"
              className={'set-button'}
              onMouseDown={(ev) => {
                ev.preventDefault()
              }}
              onClick={(ev) => {
                ev.preventDefault()
                ev.stopPropagation()
                blockBlur.current = true
                handleLinkSet(true)
                setTimeout(() => {
                  blockBlur.current = false
                }, 100)
              }}
            >
              <CheckIcon />
            </button>
          </>
        )}
      </div>
      <EditorTemplateMenu
        editor={editor}
        tags={placeholders}
        templateContext={templateContext}
        tooltip={toolbar?.template?.tooltip}
      />
      {!!onReset && (
        <button
          type="button"
          onMouseDown={(ev) => {
            ev.preventDefault()
            onReset()
          }}
        >
          <Tooltip title={__('Reset to default (generated) value')}>
            <Typography variant="overline">{__('Reset')}</Typography>
          </Tooltip>
        </button>
      )}
      <div className={classes.attachFileButtonDiv}>
        {!!onAddFiles && (
          <div
            onMouseDown={(ev) => {
              ev.preventDefault()
              onAddFiles(ev)
            }}
            className={classes.attachFileButton}
          >
            <Tooltip title={__('Attach files to the note')}>
              <>
                <Typography variant="overline" className={classes.attachFileText}>
                  {__('Attach File')}
                </Typography>
                <AttachFileIcon className={classes.attachIcon} />
              </>
            </Tooltip>
          </div>
        )}
        {showCloseIconFunctions && (
          <button
            type="button"
            onMouseDown={(ev) => {
              ev.preventDefault()
              onClear()
            }}
          >
            <Tooltip title={__('Clear note details')}>
              <CloseIcon />
            </Tooltip>
          </button>
        )}
      </div>
    </div>
  )
}
